import React, { useMemo } from "react";
import ReactSelect, { components } from "react-select";
import ReactAsyncSelect from "react-select/async";
import ReactCreatableSelect from "react-select/creatable";

import { darkSelectStyles, darkSelectTheme } from "../../styles/selectTheme";

/**
 * `react-select`, themed for the dark panel.
 *
 * Import this instead of `react-select` anywhere in the admin. The library
 * styles itself in JS, so nothing in `theme.css` reaches it — left alone it
 * renders a white control on a black page.
 *
 * A caller's own `styles` still wins: the two objects are merged per key, so a
 * page that needs one bespoke slot keeps the rest of the theme.
 */
/**
 * `markAdded` — for the add-only pickers (Picks, Summary sections, Journeys,
 * Series, Recommenders), whose chips are hidden and whose list lives below.
 *
 * A multi-select drops what is already chosen from its results, so searching
 * for a book already on the shelf found nothing — which reads as "we don't
 * have that book". With `markAdded` it stays in the results, dimmed, tagged
 * "Already added", and can't be picked: clicking a chosen option would
 * otherwise REMOVE it, unseen, from the list below.
 *
 * The tag is laid over whatever Option the caller renders rather than passed
 * into it, because the custom Options draw their own row and ignore children.
 * They are handed `isSelected={false}` so they don't paint the row green.
 */
const ADDED_TAG_STYLE = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  color: "var(--ps-text)",
  backgroundColor: "var(--ps-surface-2)",
  border: "1px solid var(--ps-border)",
};

const withAddedTag = (Base) => {
  const AddedOption = (props) => {
    if (!props.isSelected) return <Base {...props} />;
    return (
      <div style={{ position: "relative" }} title="Already added">
        <div style={{ opacity: 0.5 }}>
          <Base {...props} isSelected={false} isFocused={false} />
        </div>
        <span style={ADDED_TAG_STYLE}>Already added</span>
      </div>
    );
  };
  return AddedOption;
};

const useMarkAdded = ({ markAdded, components: given, isOptionDisabled }) => {
  const Base = given?.Option || components.Option;
  const Option = useMemo(() => withAddedTag(Base), [Base]);
  if (!markAdded) return {};
  return {
    hideSelectedOptions: false,
    components: { ...given, Option },
    isOptionDisabled: (option, selected) =>
      (selected || []).some((s) => s.value === option.value) ||
      Boolean(isOptionDisabled?.(option, selected)),
  };
};

const Select = ({ styles, theme, markAdded, ...props }) => {
  const added = useMarkAdded({ markAdded, ...props });
  return (
    <ReactSelect
      theme={theme || darkSelectTheme}
      styles={{ ...darkSelectStyles, ...(styles || {}) }}
      {...props}
      {...added}
    />
  );
};

export default Select;
export { components };

/**
 * The async and creatable entry points are separate modules, so they never saw
 * the theme when `react-select` itself was wrapped — which is why the
 * "Recommended Books" and "Select Books" controls stayed white while every
 * plain Select around them went dark.
 */
export const AsyncSelect = ({ styles, theme, markAdded, ...props }) => {
  const added = useMarkAdded({ markAdded, ...props });
  return (
    <ReactAsyncSelect
      theme={theme || darkSelectTheme}
      styles={{ ...darkSelectStyles, ...(styles || {}) }}
      {...props}
      {...added}
    />
  );
};

export const CreatableSelect = ({ styles, theme, ...props }) => (
  <ReactCreatableSelect
    theme={theme || darkSelectTheme}
    styles={{ ...darkSelectStyles, ...(styles || {}) }}
    {...props}
  />
);
